/**
 * Call UI State — ephemeral in-call UI state
 *
 * Manages local-only state for the in-call experience:
 *   - viewMode  ("grid" | "speaker" | "sidebar") for the participant layout
 *   - handRaised toggle (no backend sync yet — see spec gap)
 *   - reactions queue with 3-second auto-removal lifecycle
 *
 * State is per-call ephemeral (no persistence). Reactions are capped at
 * MAX_REACTIONS to prevent runaway growth in pathological flood cases.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type CallViewMode = "grid" | "speaker" | "sidebar";

export interface Reaction {
  id: string;
  emoji: string;
  ts: number;
  /** LiveKit participant identity this reaction is attributed to. */
  senderIdentity: string;
  /** Display name to render alongside the emoji ("You" for the local user). */
  senderName: string;
  isLocal: boolean;
}

export interface CallUiState {
  viewMode: CallViewMode;
  setViewMode: (m: CallViewMode) => void;
  handRaised: boolean;
  setHandRaised: (raised: boolean) => void;
  toggleHandRaised: () => void;
  reactions: Reaction[];
  emitReaction: (
    emoji: string,
    sender: { identity: string; name: string; isLocal: boolean },
  ) => void;
  /** Visual-only "Record meeting" toggle — no real recording backend, matches
   *  the design mock's own fake affordance (client-side toggle + toast). */
  recording: boolean;
  toggleRecording: () => void;
}

const REACTION_TTL_MS = 3000;
const MAX_REACTIONS = 30;

function generateId(): string {
  // Prefer crypto.randomUUID when available (modern browsers + Node 19+).
  // Fall back to a timestamp+random combo for older runtimes / tests.
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

export function useCallUiState(): CallUiState {
  // Default matches the design mock's own `useState('grid')` initializer.
  const [viewMode, setViewMode] = useState<CallViewMode>("grid");
  const [handRaised, setHandRaised] = useState<boolean>(false);
  const [reactions, setReactions] = useState<Reaction[]>([]);
  const [recording, setRecording] = useState<boolean>(false);

  // Track active timers so we can clear them on unmount and avoid leaks.
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const toggleHandRaised = useCallback(() => {
    setHandRaised((prev) => !prev);
  }, []);

  const toggleRecording = useCallback(() => {
    setRecording((prev) => !prev);
  }, []);

  const emitReaction = useCallback(
    (emoji: string, sender: { identity: string; name: string; isLocal: boolean }) => {
      const reaction: Reaction = {
        id: generateId(),
        emoji,
        ts: Date.now(),
        senderIdentity: sender.identity,
        senderName: sender.name,
        isLocal: sender.isLocal,
      };

      setReactions((prev) => {
        const next = [...prev, reaction];
        // Drop oldest if we're past the cap (flood protection).
        if (next.length > MAX_REACTIONS) {
          return next.slice(next.length - MAX_REACTIONS);
        }
        return next;
      });

      const timer = setTimeout(() => {
        setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
        timersRef.current.delete(timer);
      }, REACTION_TTL_MS);

      timersRef.current.add(timer);
    },
    [],
  );

  return {
    viewMode,
    setViewMode,
    handRaised,
    setHandRaised,
    toggleHandRaised,
    reactions,
    emitReaction,
    recording,
    toggleRecording,
  };
}
